<template>
  <div class="kb-board">
    <div class="kb-columns" :style="{ gridTemplateColumns: 'repeat(' + activeStages.length + ', 1fr)' }">
      <div
        v-for="stage in activeStages"
        :key="stage.key"
        class="kb-column"
        @dragover.prevent
        @dragenter.prevent
        @drop="onDrop($event, stage.key)"
      >
        <div class="kb-column__header" :style="{ borderTopColor: stage.color }">
          <span class="kb-column__dot" :style="{ background: stage.color }"></span>
          <span class="kb-column__title">{{ stage.label }}</span>
          <span class="kb-column__count">{{ columns[stage.key] ? columns[stage.key].length : 0 }}</span>
        </div>

        <div class="kb-column__body">
          <div
            v-for="lead in (columns[stage.key] || [])"
            :key="lead.id_conversa"
            :class="['kb-card',
              selectMode && !isSent(lead.id_conversa) ? 'kb-card--selectable' : '',
              isSelected(lead.id_conversa) ? 'kb-card--selected' : '',
              isSent(lead.id_conversa) ? 'kb-card--sent' : '']"
            :draggable="!selectMode"
            @dragstart="!selectMode && onDragStart($event, lead)"
            @click.stop="onCardClick(lead)"
          >
            <!-- Checkbox overlay em modo seleção -->
            <div v-if="selectMode" class="kb-card__select-check" :class="{ 'kb-card__select-check--checked': isSelected(lead.id_conversa) }">
              <svg v-if="isSelected(lead.id_conversa)" width="14" height="14" fill="none" viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>

            <div class="kb-card__top">
              <div
                class="kb-card__avatar-ring"
                :style="getLeadRingStyle(lead)"
                :title="getLeadProgressTooltip(lead)"
              >
                <div class="kb-card__avatar" :style="{ background: getColor(lead.nome) }">
                  {{ getInitials(lead.nome) }}
                </div>
              </div>
              <div class="kb-card__info">
                <div class="kb-card__name-row">
                  <div class="kb-card__name">{{ lead.nome || 'Sem nome' }}</div>
                  <span
                    class="kb-card__status"
                    :class="getLeadProgressStats(lead).completed >= getLeadProgressStats(lead).total ? 'kb-card__status--ok' : 'kb-card__status--pending'"
                  >
                    {{ getLeadProgressStats(lead).completed >= getLeadProgressStats(lead).total ? 'Completo' : 'Incompleto' }}
                  </span>
                </div>
                <div class="kb-card__flow">{{ lead.name || 'Chat' }}</div>
                <div class="kb-card__progress">{{ getLeadProgressHint(lead) }}</div>
              </div>
            </div>
            <div class="kb-card__contacts">
              <span v-if="lead.email" class="kb-card__tag kb-card__tag--email">{{ lead.email }}</span>
              <span v-if="lead.whatsapp" class="kb-card__tag kb-card__tag--phone">{{ lead.whatsapp }}</span>
            </div>
            <div class="kb-card__footer">
              <span class="kb-card__source">{{ lead.source === 'google' ? 'Google' : lead.source === 'meta' ? 'Meta' : 'Orgânico' }}</span>
              <span class="kb-card__date">{{ formatDate(lead.created_at) }}</span>
            </div>
            <div v-if="isSent(lead.id_conversa)" class="kb-card__sent-strip">
              <svg width="11" height="11" fill="none" viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Enviado ao iAqFaz
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LeadsKanban',
  props: {
    userId: [String, Number],
    enabledStages: { type: Array, default: function () { return ['novo', 'contato', 'qualificado', 'proposta', 'ganho', 'perdido'] } },
    stageConfig: { type: Array, default: function () { return [] } },
    leadCaptureConfigByChat: { type: Object, default: function () { return {} } },
    items: { type: Array, default: function () { return [] } },
    selectMode: { type: Boolean, default: false },
    selectedLeads: { type: Array, default: function () { return [] } },
    sentLeads: { type: Array, default: function () { return [] } },
  },
  data: function () {
    var defaultStages = [
      { key: 'novo', label: 'Novo', color: '#3B82F6' },
      { key: 'contato', label: 'Contato', color: '#8B5CF6' },
      { key: 'qualificado', label: 'Qualificado', color: '#F59E0B' },
      { key: 'proposta', label: 'Proposta', color: '#E86A10' },
      { key: 'ganho', label: 'Ganho', color: '#10B981' },
      { key: 'perdido', label: 'Perdido', color: '#EF4444' },
    ]
    return {
      defaultStages: defaultStages,
      localStages: {},
      stagesLoaded: false,
    }
  },
  computed: {
    allStages: function () {
      return this.stageConfig.length ? this.stageConfig : this.defaultStages
    },
    activeStages: function () {
      var self = this
      return this.allStages.filter(function (s) { return self.enabledStages.indexOf(s.key) !== -1 })
    },
    columns: function () {
      var self = this
      var cols = {}
      this.allStages.forEach(function (s) { cols[s.key] = [] })
      self.items.forEach(function (lead) {
        var stage = self.localStages[lead.id_conversa] || 'novo'
        if (cols[stage]) {
          cols[stage].push(lead)
        } else {
          cols['novo'].push(lead)
        }
      })
      return cols
    },
  },
  watch: {
    items: {
      handler: function () {
        if (this.items.length > 0) {
          this.loadStages()
        }
      },
      immediate: true,
    },
  },
  methods: {
    normalizeFieldLabel: function (label) {
      return String(label || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
    },
    getLeadRequiredFields: function (lead) {
      var byChat = this.leadCaptureConfigByChat[lead.id_chat] || []
      if (byChat.length) return byChat
      return ['Nome', 'Email', 'WhatsApp']
    },
    isLeadFieldAnswered: function (lead, requiredFieldLabel) {
      var target = this.normalizeFieldLabel(requiredFieldLabel)
      var campos = Array.isArray(lead.campos) ? lead.campos : []
      var aliases = {
        nome: ['nome', 'name'],
        email: ['email', 'e-mail', 'mail'],
        whatsapp: ['whatsapp', 'telefone', 'celular', 'phone'],
      }
      // Fallback robusto: usa campos normalizados já resolvidos do lead
      // quando labels de "campos" vierem divergentes.
      if (aliases.nome.indexOf(target) !== -1 && String(lead.nome || '').trim()) return true
      if (aliases.email.indexOf(target) !== -1 && String((lead.email || lead['e-mail'] || '')).trim()) return true
      if (aliases.whatsapp.indexOf(target) !== -1 && String(lead.whatsapp || '').trim()) return true

      var accepted = aliases[target] || [target]
      var campo = campos.find(function (c) { return accepted.indexOf(this.normalizeFieldLabel(c && c.label)) !== -1 }, this)
      if (!campo) return false
      return String(campo.value || '').trim().length > 0
    },
    getLeadProgressStats: function (lead) {
      var required = this.getLeadRequiredFields(lead)
      var total = Math.max(required.length, 1)
      var self = this
      var completed = required.reduce(function (acc, label) {
        return acc + (self.isLeadFieldAnswered(lead, label) ? 1 : 0)
      }, 0)
      return { total: total, completed: completed, pct: Math.round((completed / total) * 100) }
    },
    getLeadRingStyle: function (lead) {
      var stats = this.getLeadProgressStats(lead)
      var step = 360 / stats.total
      var gap = Math.min(6, step * 0.3)
      var fillColor = '#22c55e'
      var emptyColor = 'rgba(148, 163, 184, 0.42)'
      var sepColor = 'rgba(15, 23, 42, 0.5)'
      var segments = []
      for (var i = 0; i < stats.total; i++) {
        var start = i * step
        var segEnd = (i + 1) * step
        var fillEnd = Math.max(start, segEnd - gap)
        var color = i < stats.completed ? fillColor : emptyColor
        segments.push(color + ' ' + start + 'deg ' + fillEnd + 'deg')
        segments.push(sepColor + ' ' + fillEnd + 'deg ' + segEnd + 'deg')
      }
      return { background: 'conic-gradient(' + segments.join(',') + ')' }
    },
    getLeadProgressHint: function (lead) {
      var stats = this.getLeadProgressStats(lead)
      return stats.completed + '/' + stats.total + ' obrig.'
    },
    getLeadProgressTooltip: function (lead) {
      var stats = this.getLeadProgressStats(lead)
      return 'Coleta do lead: ' + stats.completed + '/' + stats.total + ' campos obrigatórios (' + stats.pct + '%)'
    },
    loadStages: function () {
      var self = this
      var token = localStorage.getItem('accessToken')
      var xhr = new XMLHttpRequest()
      xhr.open('GET', '/api/dkbackend/lead-pipeline/' + self.userId)
      xhr.setRequestHeader('Accept', 'application/json')
      if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token)
      xhr.onload = function () {
        if (xhr.status === 200) {
          try {
            var data = JSON.parse(xhr.responseText)
            var newMap = JSON.parse(JSON.stringify(self.localStages))
            ;(data.leads || []).forEach(function (l) {
              // Só atualiza se não foi alterado localmente (drag pendente)
              if (!self._pendingDrags || !self._pendingDrags[l.id_conversa]) {
                newMap[l.id_conversa] = l.stage || 'novo'
              }
            })
            self.localStages = newMap
            self.stagesLoaded = true
          } catch (e) {
            console.error('[Kanban]', e)
          }
        }
      }
      xhr.send()
    },

    onDragStart: function (evt, lead) {
      evt.dataTransfer.setData('text/plain', lead.id_conversa)
    },

    onDrop: function (evt, stageKey) {
      var idConversa = evt.dataTransfer.getData('text/plain')
      if (!idConversa) return

      // Guarda stage anterior para reverter se falhar
      var prevStage = this.localStages[idConversa] || 'novo'
      if (prevStage === stageKey) return

      // Atualiza localmente (otimistic update)
      var copy = JSON.parse(JSON.stringify(this.localStages))
      copy[idConversa] = stageKey
      this.localStages = copy

      // Marcar como drag pendente (protege contra reload sobrescrever)
      if (!this._pendingDrags) this._pendingDrags = {}
      this._pendingDrags[idConversa] = true

      // Salvar no backend com autenticacao
      var self = this
      var token = localStorage.getItem('accessToken')
      var xhr = new XMLHttpRequest()
      xhr.open('PUT', '/api/dkbackend/lead-pipeline/' + idConversa)
      xhr.setRequestHeader('Content-Type', 'application/json')
      if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token)
      xhr.onload = function () {
        delete self._pendingDrags[idConversa]
        if (xhr.status >= 400) {
          console.error('[Kanban] Erro ao mover lead:', xhr.responseText)
          var revert = JSON.parse(JSON.stringify(self.localStages))
          revert[idConversa] = prevStage
          self.localStages = revert
        }
      }
      xhr.onerror = function () {
        delete self._pendingDrags[idConversa]
        var revert = JSON.parse(JSON.stringify(self.localStages))
        revert[idConversa] = prevStage
        self.localStages = revert
      }
      xhr.send(JSON.stringify({ stage: stageKey, id_user: this.userId }))
    },

    isSelected: function (id_conversa) {
      return this.selectedLeads.indexOf(id_conversa) !== -1
    },
    isSent: function (id_conversa) {
      return this.sentLeads.indexOf(id_conversa) !== -1
    },
    onCardClick: function (lead) {
      if (this.selectMode) {
        if (this.isSent(lead.id_conversa)) return
        this.$emit('toggle-select', lead.id_conversa)
      } else {
        this.$router.push({
          name: 'Detalheleads',
          params: { id_lead: lead.id_conversa },
          query: this.$route.query,
        })
      }
    },
    openLead: function (lead) {
      this.$router.push({
        name: 'Detalheleads',
        params: { id_lead: lead.id_conversa },
        query: this.$route.query,
      })
    },

    getInitials: function (name) {
      if (!name || name === 'Sem nome') return '?'
      return name.split(' ').slice(0, 2).map(function (w) { return w[0] }).join('').toUpperCase()
    },

    getColor: function (name) {
      if (!name) return '#64748b'
      var colors = ['#E86A10', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#F59E0B', '#06B6D4', '#EC4899']
      var hash = 0
      for (var i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
      return colors[Math.abs(hash) % colors.length]
    },

    formatDate: function (d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
    },
  },
}
</script>

<style lang="scss" scoped>
$orange: #E86A10;
.kb-board { padding: 0 0 24px; }
.kb-columns { display: grid; gap: 12px; }
.kb-column { background: #f1f5f9; border-radius: 12px; min-width: 0; }
.kb-column__header { display: flex; align-items: center; gap: 8px; padding: 14px 16px; border-top: 3px solid #ccc; border-radius: 12px 12px 0 0; background: #fff; }
.kb-column__dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.kb-column__title { font-size: 13px; font-weight: 700; color: #1e293b; flex: 1; }
.kb-column__count { font-size: 12px; font-weight: 700; color: #64748b; background: #f1f5f9; padding: 2px 8px; border-radius: 6px; }
.kb-column__body { padding: 8px; display: flex; flex-direction: column; gap: 8px; min-height: 80px; }
.kb-card { background: #fff; border-radius: 10px; padding: 14px; cursor: grab; border: 1px solid #e2e8f0; transition: box-shadow 0.2s, transform 0.15s, border-color 0.15s; position: relative; }
.kb-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); transform: translateY(-1px); }
.kb-card:active { cursor: grabbing; }
.kb-card--selectable { cursor: pointer; }
.kb-card--selectable:hover { border-color: #7c3aed; box-shadow: 0 0 0 2px rgba(124,58,237,0.12); transform: none; }
.kb-card--selected { border-color: #7c3aed !important; background: rgba(124,58,237,0.04) !important; box-shadow: 0 0 0 2px rgba(124,58,237,0.2) !important; transform: none !important; }
.kb-card--sent { cursor: default !important; }
.kb-card--sent:hover { box-shadow: none !important; transform: none !important; border-color: rgba(124,58,237,0.3) !important; }
.kb-card__sent-strip {
  display: flex; align-items: center; gap: 5px;
  margin: 10px -14px -14px; padding: 6px 14px;
  background: rgba(124,58,237,0.07); border-top: 1px solid rgba(124,58,237,0.15);
  border-radius: 0 0 9px 9px; color: #7c3aed;
  font-size: 11px; font-weight: 700;
}
.kb-card__select-check {
  position: absolute; top: 10px; right: 10px;
  width: 20px; height: 20px; border-radius: 5px;
  border: 2px solid #cbd5e1; background: #fff;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
  &--checked { background: #7c3aed; border-color: #7c3aed; color: #fff; }
}
.kb-card__top { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.kb-card__avatar-ring {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 3px;
}
.kb-card__avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
}
.kb-card__name-row { display: flex; align-items: center; gap: 6px; }
.kb-card__name { font-size: 13.5px; font-weight: 700; color: #0f172a; line-height: 1.2; }
.kb-card__flow { font-size: 11px; color: #94a3b8; font-weight: 500; }
.kb-card__progress { font-size: 10px; color: #10b981; font-weight: 700; margin-top: 1px; }
.kb-card__contacts { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px; }
.kb-card__tag { font-size: 11px; padding: 2px 8px; border-radius: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.kb-card__tag--email { background: #EFF6FF; color: #2563EB; }
.kb-card__tag--phone { background: #ECFDF5; color: #059669; }
.kb-card__footer { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.kb-card__source { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 4px; background: #FFF7ED; color: $orange; text-transform: uppercase; }
.kb-card__status {
  font-size: 8.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  padding: 1px 5px;
  border-radius: 999px;
  border: 1px solid transparent;
  line-height: 1.3;
  flex-shrink: 0;
  &--ok {
    color: #10b981;
    background: rgba(16,185,129,0.12);
    border-color: rgba(16,185,129,0.28);
  }
  &--pending {
    color: #94a3b8;
    background: rgba(148,163,184,0.14);
    border-color: rgba(148,163,184,0.3);
  }
}
.kb-card__date { font-size: 11px; color: #94a3b8; }

@media (max-width: 1200px) {
  .kb-card__name-row { gap: 4px; }
  .kb-card__status {
    font-size: 8px;
    padding: 1px 4px;
    letter-spacing: .02em;
  }
}
</style>
